import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { VerandaShapeId, WindowShapeId } from '@core/constants/Shape';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import { MaterialId } from '@core/constants/Material';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
} from '../../interfaces/Form';
import additionalDataFactory from './common/fieldsAdditionalData';
import fieldsContact from './common/fieldsContact';
import {
    allGlasses,
    allMetals,
    allPlastics,
    allSolidWoods,
    allStones,
    getMaterialsWithExceptions,
} from './common/materialGroups';

const VerandaForm: IForm = {
    formId       : PublishedFormId.Veranda,
    type         : FormTypeId.GlassAndMirror,
    thumbnailURL : '/resized-images/forms/veranda.jpg',
    imagesURLs   : [
        '/resized-images/forms/description/veranda/veranda1.jpg',
        '/resized-images/forms/description/veranda/veranda2.jpg',
    ],
    fieldsOrder: [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.GroundShapeDimension,
        IFormFieldKey.RoofShapeDimension,
        IFormFieldKey.Material,
        IFormFieldKey.FrameMaterial,
        IFormFieldKey.Openings,
        IFormFieldKey.AddOns,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        groundShapeDimension: {
            shape: {
                value   : undefined,
                options : [
                    VerandaShapeId.SquareShape,
                    VerandaShapeId.RectangleShape,
                    VerandaShapeId.LShape,
                    VerandaShapeId.UShape,
                    VerandaShapeId.TShape,
                ],
                required: true,
            },
            dimension: {
                options: {
                    [VerandaShapeId.SquareShape]: {
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                    },
                    [VerandaShapeId.RectangleShape]: {
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Length]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                    },
                    [VerandaShapeId.LShape]: {
                        [FieldDimensionId.Width1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Length1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Length2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                    },
                    [VerandaShapeId.UShape]: {
                        [FieldDimensionId.Length1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 8000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Length2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 8000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Length3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 8000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                    },
                    [VerandaShapeId.TShape]: {
                        [FieldDimensionId.Width1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Length1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Length2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Length3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1500, max: 6000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                    },
                },
            },
        },
        roofShapeDimension: {
            shape: {
                value   : undefined,
                options : [
                    VerandaShapeId.FlatRoofVeranda,
                    VerandaShapeId.SlopingRoofVeranda,
                    VerandaShapeId.DoubleRoofVeranda,
                ],
                required: true,
            },
            dimension: {
                options: {
                    [VerandaShapeId.FlatRoofVeranda]: {
                        [FieldDimensionId.HeightWall]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 1800, max: 3000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 2,
                            range        : { min: 14, max: 30 },
                            required     : true,
                            defaultValue : 20,
                        },
                    },
                    [VerandaShapeId.SlopingRoofVeranda]: {
                        [FieldDimensionId.HeightRoofTop]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 1800, max: 3000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.HeightWall]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 1800, max: 3000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 2,
                            range        : { min: 8, max: 20 },
                            required     : true,
                            defaultValue : 10,
                        },
                    },
                    [VerandaShapeId.DoubleRoofVeranda]: {
                        [FieldDimensionId.HeightRoofTop]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 1800, max: 3000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.HeightWall]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 1800, max: 3000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 2,
                            range        : { min: 8, max: 20 },
                            required     : true,
                            defaultValue : 10,
                        },
                    },
                },
            },
        },
        material: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allGlasses,
                        ...allPlastics,
                        ...allSolidWoods,
                        ...allMetals,
                        ...allStones,
                        MaterialId.Terracotta,
                    ],
                    [
                        MaterialId.OakRegular,
                        MaterialId.Beech,
                        MaterialId.WagonfloorDark,
                        MaterialId.WagonfloorLight,
                        MaterialId.OakOld,
                        MaterialId.Walnut,
                        MaterialId.Ash,
                        MaterialId.AshOlive,
                        MaterialId.Chestnut,
                        MaterialId.Acacia,
                        MaterialId.Mahogany,
                        MaterialId.Elm,
                        MaterialId.Alderwood,
                        MaterialId.Birch,
                        MaterialId.Maple,
                        MaterialId.Teak,
                        MaterialId.Wenge,
                        MaterialId.Cherrywood,
                        MaterialId.Ebony,
                        MaterialId.Rubber,
                        MaterialId.DriftWood,
                        MaterialId.Poplar,
                        MaterialId.Ipe,
                        MaterialId.Padouk,
                        MaterialId.Redcedar,
                        MaterialId.BlackLocust,
                        MaterialId.Steel,
                        MaterialId.Rust,
                        MaterialId.Brass,
                        MaterialId.ThermoLaqueredBlack,
                        MaterialId.ThermoLaqueredWhite,
                        MaterialId.SheetMetal,
                        MaterialId.StainlessSteel,
                        MaterialId.Zinc,
                        MaterialId.PlexiglassFluorescent,
                        MaterialId.PlexiglassFrosted,
                        MaterialId.PlexiglassMirror,
                        MaterialId.PlexiglassTransparent,
                        MaterialId.PlexiglassWhite,
                        MaterialId.RecycledPlastic,
                        MaterialId.Pvc,
                        MaterialId.PvcRigid,
                        MaterialId.StoneBlueHainot,
                        MaterialId.StoneBourgogne,
                        MaterialId.StoneLens,
                        MaterialId.StoneLuserne,
                        MaterialId.StoneSerpentinite,
                    ],
                ),
                required: true,
            },
        },
        frameMaterial: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allSolidWoods,
                        ...allMetals,
                        MaterialId.Pvc,
                        MaterialId.PvcRigid,
                    ],
                    [
                        MaterialId.Poplar,
                        MaterialId.DriftWood,
                        MaterialId.WagonfloorDark,
                        MaterialId.WagonfloorLight,
                        MaterialId.OakOld,
                        MaterialId.Walnut,
                        MaterialId.Ash,
                        MaterialId.AshOlive,
                        MaterialId.Chestnut,
                        MaterialId.Acacia,
                        MaterialId.Mahogany,
                        MaterialId.Elm,
                        MaterialId.Alderwood,
                        MaterialId.Birch,
                        MaterialId.Maple,
                        MaterialId.Wenge,
                        MaterialId.Cherrywood,
                        MaterialId.Ebony,
                        MaterialId.Rubber,
                        MaterialId.Rust,
                        MaterialId.Brass,
                        MaterialId.SheetMetal,
                        MaterialId.StainlessSteel,
                        MaterialId.Zinc,
                    ],
                ),
                required: true,
            },
        },
        openings: {
            [WindowShapeId.SingleWindow]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WindowShapeId.DoubleWindow]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WindowShapeId.SlidingWindow]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [WindowShapeId.SimpleWindowDoor]: {
                value : undefined,
                min   : 0,
                max   : 4,
            },
            [WindowShapeId.DoubleWindowDoor]: {
                value : undefined,
                min   : 0,
                max   : 4,
            },
            [WindowShapeId.SlidingWindowDoor]: {
                value : undefined,
                min   : 0,
                max   : 4,
            },
        },
        addOns: {
            [VerandaShapeId.VerandaHeater]: {
                value : undefined,
                min   : 0,
                max   : 5,
            },
            [VerandaShapeId.VerandaStore]: {
                value : undefined,
                min   : 0,
                max   : 4,
            },
            [VerandaShapeId.VerandaBurglarProofGlazing]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
            [VerandaShapeId.VearndaLighting]: {
                value : undefined,
                min   : 0,
                max   : 10,
            },
            [VerandaShapeId.VearndaThermalInsulation]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
        },
        additionalData: additionalDataFactory({
            budgetMin          : 5000,
            budgetMax          : 30000,
            budgetDefaultValue : 12000,
            budgetStep         : 500,
        }),
        contact: fieldsContact,
    },
};

export default VerandaForm;
